/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';

import * as API from '@ibm-watson/shared/modules/ia/api/preferences';
import { selectAllAssistantIds } from '@ibm-watson/shared/modules/ia/store/assistants/core-selectors';
import { isCacheExpired } from '@ibm-watson/shared/modules/ia/store/helper';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { AssistantPreferencesWithId } from '../../../../ts/preferences';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import { selectAssistantPreferencesById } from './selectors';

export const listAllAssistantPreferences =
  (): AsyncAppThunk => (dispatch, getState) => {
    try {
      const assistantIds = selectAllAssistantIds(getState());
      assistantIds.forEach(assistantId =>
        dispatch(listAssistantPreferencesIfRequired(assistantId))
      );
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  };

export const listAssistantPreferences = createAsyncThunk<
  AssistantPreferencesWithId,
  string,
  CreateAsyncThunk
>('preferences/listAssistantPreferences', async (assistantId, { dispatch }) => {
  try {
    const response = await API.listAssistantPreferences(assistantId);
    return response;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listAssistantPreferencesIfRequired =
  (assistantId: string): AsyncAppThunk<Promise<AssistantPreferencesWithId>> =>
  async (dispatch, getState) => {
    try {
      const { assistantId: cachedAssistantId, listFetched } =
        getState()?.preferences;
      // Avoid requests if data is not stale
      if (assistantId === cachedAssistantId && !isCacheExpired(listFetched)) {
        return Promise.resolve(
          selectAssistantPreferencesById(getState(), assistantId)
        );
      }
      await dispatch(listAssistantPreferences(assistantId));
      return selectAssistantPreferencesById(getState(), assistantId);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  };

export const setAssistantPreferences = createAsyncThunk<
  AssistantPreferencesWithId,
  AssistantPreferencesWithId,
  CreateAsyncThunk
>(
  'preferences/setAssistantPreferences',
  async ({ assistant_id, preferences }, { dispatch }) => {
    try {
      // note that the set will return the new pref set
      const response = await API.setAssistantPreferences(
        assistant_id,
        preferences
      );
      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);
